import React, { useState } from 'react';
import { Button } from '../../ui/button';
import DatePickerField from '@/components/atoms/DatePickerField';

import checkSuccessImg from '@/assets/images/check-success.svg';

type ModalVariant = 'success' | 'failure';

interface ConfirmModalProps {
  isOpen: boolean;
  variant?: ModalVariant;
  title: string;
  message: string | React.ReactNode;
  buttonText: string | React.ReactNode;
  onConfirm: (paymentDate?: Date | null) => void;
  icon?: string | React.ReactNode;
  onClose?: () => void;
  loading?: boolean;
  hasPaymentDate?: boolean;
  /** When invoice has no payment date, use this as minDate for the payment date picker (e.g. issue date). */
  paymentIssueDate?: Date | string | null;
}

const ConfirmModal: React.FC<ConfirmModalProps> = ({
  isOpen,
  variant = 'success',
  title,
  message,
  buttonText,
  onConfirm,
  icon,
  onClose = () => {},
  loading = false,
  hasPaymentDate = true,
  paymentIssueDate
}) => {
  const [selectedPaymentDate, setSelectedPaymentDate] = useState<Date | null>(null);

  if (!isOpen) return null;

  // two variants for now
  const getDefaultIcon = (variant: ModalVariant) => {
    switch (variant) {
      case 'success':
        return checkSuccessImg;
      case 'failure':
        return checkSuccessImg;
      default:
        return checkSuccessImg;
    }
  };

  const getButtonColorClass = (variant: ModalVariant) => {
    switch (variant) {
      case 'success':
        return 'bg-primary hover:bg-primary-dark';
      case 'failure':
        return 'bg-red-500 hover:bg-red-600';
      default:
        return 'bg-primary hover:bg-primary-dark';
    }
  };

  return (
    <div className="fixed w-screen h-screen top-0 left-0 bg-[rgba(82,80,96,0.5)] backdrop-blur-[8px] z-50 flex items-center justify-center">
      <div className="bg-white rounded-[16px] overflow-hidden w-[327px]">
        <div className="w-full py-7 px-7 md:p-0 flex flex-col items-center justify-center gap-2">
          <div className="md:bg-secondary w-full md:mb-4 md:py-10 flex items-center justify-center">
            {typeof icon === 'string' ? (
              <img
                className="w-[60px] h-[60px] md:w-auto md:h-auto mb-3 mx-auto"
                src={icon || getDefaultIcon(variant)}
                alt={`${variant}-icon`}
              />
            ) : (
              icon
            )}
          </div>
          <p className="md:px-7 font-bold text-xl text-center leading-[24.3px] md:mb-[0.15rem]">{title}</p>
          <p className="md:px-7 text-sm text-secondary text-center leading-[17px]">{message}</p>
        </div>
        {!hasPaymentDate && (
          <div className="px-5 pb-2 mt-2">
            <DatePickerField
              name="payment_date"
              label="Payment date"
              value={selectedPaymentDate}
              onChange={setSelectedPaymentDate}
              minDate={paymentIssueDate ? new Date(paymentIssueDate) : undefined}
              size="sm"
              placeholder="Select date"
              className="w-full"
            />
          </div>
        )}
        <div className="flex md:gap-4 md:px-5 md:mb-6 md:mt-7">
          <Button
            onClick={onClose}
            className={`w-full text-base font-semibold !rounded-none h-[52px] md:w-full md:text-[1rem] md:font-semibold md:!rounded-full md:h-[44px]`}
            variant={'primary-reverse'}
            disabled={loading}
          >
            {'Cancel'}
          </Button>

          <Button
            onClick={() => (hasPaymentDate ? onConfirm() : onConfirm(selectedPaymentDate))}
            className={`w-full text-base font-semibold !rounded-none h-[52px] md:w-full md:text-[1rem] md:font-semibold md:!rounded-full md:h-[44px] `}
            variant={'default'}
            disabled={loading || (!hasPaymentDate && !selectedPaymentDate)}
          >
            {buttonText}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmModal;
